import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getImages, getImageDescriptions, saveImageDescription, deleteImageTag } from './api';
import { formatBytes } from './utils';
import DeleteConfirmModal from './DeleteConfirmModal';
import CreateVMModal from './CreateVMModal';

// OrkaHub public images
const ORKAHUB_IMAGES = [
  {
    name: 'orkahub/sonoma',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/sonoma:latest',
    size: 0,
    isOrkaHub: true
  },
  {
    name: 'orkahub/sequoia',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/sequoia:latest',
    size: 0,
    isOrkaHub: true
  },
  {
    name: 'orkahub/tahoe',
    tag: 'latest',
    full_name: 'ghcr.io/macstadium/orka-images/tahoe:latest',
    size: 0,
    isOrkaHub: true
  }
];

function CustomerImageCard({ image, description, onDeleteClick, onDeployClick }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(description || '');

  // Keep the local field in sync once the descriptions map loads / changes.
  useEffect(() => {
    setText(description || '');
  }, [description]);

  const saveMutation = useMutation({
    mutationFn: (desc) => saveImageDescription(image.name, desc),
    onSuccess: () => {
      queryClient.invalidateQueries(['image-descriptions']);
    },
  });

  // Save on blur (and Enter, which blurs) - only when the value actually changed.
  const commitDescription = () => {
    if (text !== (description || '')) {
      saveMutation.mutate(text);
    }
  };

  return (
    <div className="card">
      <div className="card-title">
        <span>📦</span>
        <span>{description || image.name}</span>
      </div>

      <div className="card-content">
        <div className="info-row">
          <span className="info-label">Image</span>
          <span className="info-value" style={{ fontSize: '0.8rem' }}>{image.name}</span>
        </div>

        <div className="info-row">
          <span className="info-label">Description</span>
          <input
            className="orka-desc-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commitDescription}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            placeholder="Describe this image"
            disabled={saveMutation.isPending}
          />
        </div>

        <div className="info-row">
          <span className="info-label">Tag</span>
          <span className="info-value">
            <span className="badge badge-info">{image.tag}</span>
          </span>
        </div>

        <div className="info-row">
          <span className="info-label">Size</span>
          <span className="info-value">{formatBytes(image.size)}</span>
        </div>

        <div className="info-row">
          <span className="info-label">Full Name</span>
          <span className="info-value" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
            <code>{image.full_name}</code>
          </span>
        </div>

        {image.push_time && (
          <div className="info-row">
            <span className="info-label">Pushed</span>
            <span className="info-value">{new Date(image.push_time).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="card-actions">
        <button className="btn btn-primary btn-small" onClick={() => onDeployClick(image)}>
          Deploy
        </button>
        <button className="btn btn-danger btn-small" onClick={() => onDeleteClick(image)}>
          Delete
        </button>
      </div>
    </div>
  );
}

function ImagesView() {
  const queryClient = useQueryClient();
  const [sortBy, setSortBy] = useState('name');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deployTarget, setDeployTarget] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['images'],
    queryFn: async () => {
      const response = await getImages();
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: descMap = {} } = useQuery({
    queryKey: ['image-descriptions'],
    queryFn: async () => (await getImageDescriptions()).data,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ name, tag }) => deleteImageTag(name, tag),
    onSuccess: () => {
      queryClient.invalidateQueries(['images']);
      setDeleteTarget(null);
    },
  });

  const images = useMemo(() => {
    const items = [...(data?.items || [])];
    if (sortBy === 'date') {
      // Newest first; images without a push_time sort last.
      items.sort((a, b) => new Date(b.push_time || 0) - new Date(a.push_time || 0));
    } else {
      items.sort((a, b) => a.name.localeCompare(b.name) || a.tag.localeCompare(b.tag));
    }
    return items;
  }, [data, sortBy]);

  if (isLoading) {
    return <div className="loading">Loading images...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <strong>Error loading images:</strong> {error.message}
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Images</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label htmlFor="image-sort" style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Sort by</label>
          <select id="image-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="date">Creation date</option>
          </select>
          <button className="btn btn-secondary" onClick={() => refetch()}>
            Refresh
          </button>
        </div>
      </div>

      {/* Customer Harbor Images */}
      {images.length > 0 && (
        <>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '1rem',
            marginTop: '0.5rem'
          }}>
            Customer Images
          </h3>
          <div className="card-grid" style={{ marginBottom: '2rem' }}>
            {images.map((image, idx) => (
              <CustomerImageCard
                key={`${image.name}-${image.tag}-${idx}`}
                image={image}
                description={descMap[image.name]}
                onDeleteClick={setDeleteTarget}
                onDeployClick={setDeployTarget}
              />
            ))}
          </div>
        </>
      )}

      {/* OrkaHub Images */}
      <h3 style={{
        fontSize: '1.125rem',
        fontWeight: '600',
        color: '#fff',
        marginBottom: '1rem',
        marginTop: images.length > 0 ? '1rem' : '0.5rem'
      }}>
        OrkaHub Images
      </h3>
      <div className="card-grid">
        {ORKAHUB_IMAGES.map((image, idx) => (
          <div key={`orkahub-${idx}`} className="card">
            <div className="card-title">
              <span>📦</span>
              <span>{image.name}</span>
            </div>

            <div className="card-content">
              <div className="info-row">
                <span className="info-label">Tag</span>
                <span className="info-value">
                  <span className="badge badge-info">{image.tag}</span>
                </span>
              </div>

              <div className="info-row">
                <span className="info-label">Source</span>
                <span className="info-value">
                  <span className="badge badge-success">OrkaHub</span>
                </span>
              </div>

              <div className="info-row">
                <span className="info-label">Full Name</span>
                <span className="info-value" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  <code>{image.full_name}</code>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deployTarget && (
        <CreateVMModal
          initialImage={deployTarget.full_name}
          onClose={() => setDeployTarget(null)}
          onSuccess={() => {
            setDeployTarget(null);
            queryClient.invalidateQueries(['vms']);
            queryClient.invalidateQueries(['nodes']);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Delete Image"
          message={`Are you sure you want to delete image "${deleteTarget.name}:${deleteTarget.tag}" from Harbor? This action cannot be undone.`}
          onConfirm={() => deleteMutation.mutate({ name: deleteTarget.name, tag: deleteTarget.tag })}
          onCancel={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

export default ImagesView;
